import { ParamEnum } from '@interface/public';

const resumeKey = 'website_customer_login_resume';
const callbackParam = 'dh_login';
const lifetime = 10 * 60 * 1000;
// 当前页面就是 SSO 登录回调页（弹窗）时置位，用于阻止弹窗里再自动展开数字人客服。
let callbackPage = false;
export const isCustomerLoginCallback = () => callbackPage;
export interface LoginUser { token: string; userID: string; username: string }

/** SSO 回到同源页面时通知发起窗口；来源与 nonce 在发起端一起校验。 */
export function completeCustomerLogin(href: string, user: LoginUser): void {
  const url = new URL(href);
  const nonce = url.searchParams.get(callbackParam) || new URLSearchParams(url.hash.split('?')[1] || '').get(callbackParam);
  if (!nonce || !user.token || !user.userID || !user.username) return;
  callbackPage = true;
  const message = { type: 'website-customer-login', nonce, user };
  window.opener?.postMessage(message, location.origin);
  // SSO 的 COOP 策略可能切断 opener；同源、随机 nonce 的频道仍可恢复原页登录态。
  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(`website-customer-login-${nonce}`);
    const closeSelf = () => { channel.close(); window.close(); };
    const timer = setTimeout(closeSelf, 2500); // 发起端确认前兜底关闭，避免弹窗停留在官网页
    channel.onmessage = event => { if (event.data?.type === 'accepted') { clearTimeout(timer); closeSelf(); } };
    channel.postMessage(message);
  }
}

export function consumeCustomerLoginResume(): boolean {
  try {
    const value = JSON.parse(sessionStorage.getItem(resumeKey) || 'null');
    if (!value || Date.now() - value.time > lifetime) { sessionStorage.removeItem(resumeKey); return false; }
    if (!$RQ.SSO.check()) return false;
    sessionStorage.removeItem(resumeKey);
    return true;
  } catch { sessionStorage.removeItem(resumeKey); return false; }
}

/** 只有业务窗口持有 popup 引用与随机 nonce，不接受其他页面发来的登录消息。 */
export function beginCustomerLogin(onSuccess: (user: LoginUser) => void, onCancel: () => void) {
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('');
  const callback = new URL(location.href);
  [ParamEnum.Token, ParamEnum.UserID, ParamEnum.UserName, ParamEnum.Operate, callbackParam].forEach(key => callback.searchParams.delete(key));
  callback.searchParams.set(callbackParam, nonce);
  sessionStorage.setItem(resumeKey, JSON.stringify({ time: Date.now() }));
  let popup: Window | null = null;
  let timer: ReturnType<typeof setInterval> | undefined;
  let finished = false;
  const channel = 'BroadcastChannel' in window ? new BroadcastChannel(`website-customer-login-${nonce}`) : null;
  const cleanup = () => { finished = true; window.removeEventListener('message', receive); if (timer) clearInterval(timer); channel?.close(); };
  const receive = (event: MessageEvent) => {
    if (finished || event.origin !== location.origin || event.source !== popup || event.data?.type !== 'website-customer-login' || event.data.nonce !== nonce) return;
    accept(event.data);
  };
  const accept = (data: any) => {
    if (finished || data?.type !== 'website-customer-login' || data.nonce !== nonce) return;
    const user = data.user;
    if (!user || !['token', 'userID', 'username'].every(key => typeof user[key] === 'string' && user[key].length > 0)) return;
    channel?.postMessage({ type: 'accepted' });
    // BroadcastChannel 投递是异步的，立刻 close 可能丢掉 accepted，延迟清理让弹窗可靠关闭。
    setTimeout(cleanup, 800);
    finished = true;
    sessionStorage.removeItem(resumeKey);
    onSuccess({ token: user.token, userID: user.userID, username: user.username });
    popup?.close(); window.focus();
  };
  if (channel) channel.onmessage = event => accept(event.data);
  window.addEventListener('message', receive);
  popup = $RQ.SSO.login(callback.href, '', true, 'popup=yes,width=520,height=720', $Config.DigitalHuman.sso);
  if (!popup) { cleanup(); $RQ.SSO.login(callback.href, '', false, '', $Config.DigitalHuman.sso); }
  else {
    const started = Date.now();
    timer = setInterval(() => {
      if ((popup?.closed && !channel) || Date.now() - started > lifetime) { cleanup(); sessionStorage.removeItem(resumeKey); onCancel(); }
    }, 500);
  }
  return () => { cleanup(); popup?.close(); if (popup) sessionStorage.removeItem(resumeKey); };
}
